<template>
  <div v-if="openModal" @keydown="onKeydownEscape" v-bind="$attrs">
    <div :class="maskBgClass">
      <div class="modal-wrapper" @click.self="handleClickBackdrop(true)">
        <focus-loop :disabled="!openModal">
          <div
            :aria-label="ariaLabel"
            :class="modalBgClass"
            role="dialog"
            tabindex="0"
          >
            <slot name="content" :hide="hide"></slot>
          </div>
        </focus-loop>
      </div>
    </div>
  </div>
</template>

<script>
import { FocusLoop } from '@vue-a11y/focus-loop';

export default {
  name: 'Modal',
  components: { FocusLoop },
  props: {
    modalVariant: {
      type: String,
      required: false,
      default: null,
    },
    ariaLabel: {
      type: String,
      default: 'Modal',
    },
    noCloseOnBackdrop: {
      type: Boolean,
      required: false,
      default: false,
    },
    returnFocusOnCloseElement: {
      type: String,
      required: false,
    },
    maskVariant: {
      type: String,
      required: false,
      default: '',
    },
    noBackdrop: {
      type: Boolean,
      required: false,
      default: false,
    },
    whiteBlur: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasShadow: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalPadding: {
      type: Boolean,
      required: false,
      default: false,
    },
    blockEscape: {
      type: Boolean,
      required: false,
      default: false,
    },
    audioCloseId: {
      type: String,
      default: 'button-click',
    },
    notOverlapped: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      openModal: false,
    };
  },
  methods: {
    show() {
      this.openModal = true;
      this.$emit('onShowModal', this);

      this.$nextTick(() => {
        document.body.classList.add('modal-open');
      });
    },
    handleClickBackdrop(clickOnBackdrop) {
      if (clickOnBackdrop && this.noCloseOnBackdrop) {
        return;
      }

      this.hide();
    },
    hide() {
      if (this.audioCloseId) {
        this.getAudioById(this.audioCloseId).play();
      }

      this.returnFocusOnClose();
      this.openModal = false;

      this.$emit('onHideModal');

      this.$nextTick(() => {
        document.body.classList.remove('modal-open');
      });
    },
    returnFocusOnClose() {
      if (this.returnFocusOnCloseElement) {
        const el = document.getElementById(this.returnFocusOnCloseElement);
        if (el) {
          el.focus();
          return;
        }
      }
      this.focusFirstFocusableElement();
    },
    onKeydownEscape(e) {
      if (!this.blockEscape && e.keyCode === 27) {
        this.hide();
      }
    },
  },
  computed: {
    modalBgClass() {
      const classes = {
        'modal-container': true,
      };
      classes[`bg-${this.modalVariant}`] = !!this.modalVariant;
      classes['has-shadow'] = this.hasShadow;
      classes['modal-padding'] = this.modalPadding;

      return classes;
    },
    maskBgClass() {
      const classes = {
        'modal-mask': true,
      };
      classes['modal-mask__not-backdrop'] = this.noBackdrop;
      classes[`bg-${this.maskVariant}`] = !!this.maskVariant;
      classes['modal-mask__firefox-ie'] = this.isFirefoxOrExplorer;
      classes['modal-mask__white-blur'] = this.whiteBlur;
      classes['modal-mask--not-overlapped'] = this.notOverlapped;

      return classes;
    },
    isFirefoxOrExplorer() {
      const isFirefox = typeof InstallTrigger !== 'undefined';
      const isIE = !!document.documentMode;

      return isFirefox || isIE;
    },
  },
};
</script>

<style lang="stylus" scoped>
@import "~@discoveryeducation/theme/src/mixins.styl"

.modal-mask
  position fixed
  top 0
  bottom 0
  left 0
  right 0
  display flex;
  justify-content center;
  align-items center;
  z-index 9998
  transition opacity .3s ease

  &:not(.modal-mask__not-backdrop)
    backdrop-filter blur(15px)
    -webkit-backdrop-filter blur(15px)

  &:not([class*='bg-'])
    &.modal-mask__firefox-ie
      background rgba(0, 0, 0, 0.8)

      &.modal-mask__white-blur
        background rgba(255, 255, 255, 0.6)

  &--not-overlapped
    z-index auto

.modal-wrapper
  flexbox(flex)
  justify-content(center)
  align-items(center)
  height 100%
  width 100%
  vertical-align middle

.modal-container
  padding 3.125em
  border-radius 10px
  transition all .3s ease
  color colors.white

  &.has-shadow
    box-shadow(0px 5px 30px #00000059)

.modal-padding
  padding 0
</style>
